<script setup lang="ts">
import { OTranscript, useDateTime, type OTranscriptProps } from '@ai/ui-components'
import type { ActiveCall } from 'shared/src'
import Disconnect from '@/assets/img/svg/icons/disconnect.svg'
import { usePhoneFormatter } from '@/composables/formatting/usePhoneFormatter'
import { useLanguages } from '@/composables/translations/useLanguages'
import CallDetailsNavigation from './CallDetailsNavigation.vue'
import CallDetailsMetadataCard, { type CallDetailsMetadataRow } from './CallDetailsMetadataCard.vue'
import { useCallDetailsRightPanelClass } from './useCallDetailsRightPanelClass'

const open = defineModel<boolean>('open', { default: false })

const props = withDefaults(
  defineProps<{
    call?: ActiveCall
    agentName?: string
    statusColor?: 'success' | 'warning'
    statusLabel?: string
    leftDisabled?: boolean
    rightDisabled?: boolean
    loading?: boolean
    disconnected?: boolean
    listening?: boolean
    audio?: OTranscriptProps['audio']
    transcript?: OTranscriptProps['transcript']
    events?: OTranscriptProps['events']
  }>(),
  {
    agentName: '',
    statusColor: 'success',
    statusLabel: '',
    leftDisabled: false,
    rightDisabled: false,
    loading: false,
    disconnected: false,
    listening: false,
    audio: null,
    transcript: () => [],
    events: () => [],
  },
)

const emit = defineEmits<{
  close: []
  navigate: [direction: 'left' | 'right']
  toggleListening: []
  disconnect: []
}>()

const { t, locale } = useI18n()
const { formatTimerDuration } = useDateTime()
const { formatPhone } = usePhoneFormatter()
const { languageLabel } = useLanguages()

const agentLabel = computed(() => `${t('agent')}: ${props.agentName}`)
const panelClass = useCallDetailsRightPanelClass(open)
const metadataRows = computed<CallDetailsMetadataRow[]>(() => [
  {
    id: 'active-call-caller-number',
    label: t('contact_number'),
    value: formatPhone(props.call?.callerNumber),
    private: true,
  },
  {
    id: 'active-call-language',
    label: t('language'),
    value: props.call ? languageLabel(locale.value, props.call.language) : '-',
  },
])

const updateOpen = (value: boolean | undefined) => {
  open.value = Boolean(value)
  if (!value) emit('close')
}
</script>

<template>
  <ORightPanel
    :open="open"
    :title="t('call_details')"
    floating
    hide-header-divider
    :panel-class="panelClass"
    header-class="pb-l pl-6"
    content-class="-mb-6 min-h-0 overflow-hidden radius-b-xl pl-0 pr-0 w-[calc(100%+1.5rem)]"
    @update:open="updateOpen"
  >
    <template #title-extra>
      <div class="flex items-center gap-s">
        <CallDetailsNavigation
          :left-disabled="leftDisabled"
          :right-disabled="rightDisabled"
          @navigate="emit('navigate', $event)"
        />
        <OBadge
          v-if="statusLabel"
          variant="secondary"
          :color="statusColor"
          :content="statusLabel"
        />
      </div>
    </template>

    <template #header>
      <slot name="audio-renderer" />

      <CallDetailsMetadataCard
        :title="agentLabel"
        :rows="metadataRows"
        gap-class="gap-s"
        title-testid="agent-name-value"
        data-testid="active-call-details-card"
      >
        <template #header>
          <div
            class="flex w-full flex-col items-center justify-center gap-xs text-neutral-1"
            data-testid="active-call-status-timer"
          >
            <span class="typography-body-1">{{
              disconnected ? t('call_ended') : t('live_call')
            }}</span>
            <span class="typography-display-1">
              {{
                formatTimerDuration({
                  value: call?.durationSec,
                  alwaysIncludeHours: true,
                  fallback: '00:00:00',
                })
              }}
            </span>
          </div>
        </template>

        <template #footer>
          <div class="grid w-full grid-cols-2 gap-3">
            <OButton
              size="L"
              :label="listening ? t('stop_listening_call') : t('listen')"
              :variant="listening ? 'primary' : 'secondary'"
              :disabled="disconnected"
              dataTestid="active-call-listen"
              @click="emit('toggleListening')"
            >
              <template #icon><UiIcon name="audio-on" variant="solid" size="l" /></template>
            </OButton>
            <OButton
              size="L"
              :label="t('disconnect')"
              variant="secondary"
              :disabled="disconnected"
              dataTestid="active-call-disconnect"
              @click="emit('disconnect')"
            >
              <template #icon><Disconnect class="size-4" /></template>
            </OButton>
          </div>
        </template>
      </CallDetailsMetadataCard>
    </template>

    <div v-if="loading" class="flex h-full min-h-0 items-start justify-center pt-16">
      <OSpinner size="XL" />
    </div>
    <div v-else class="flex h-full min-h-0 flex-col overflow-hidden">
      <div class="min-h-0 flex-1 border-t border-neutral-5">
        <OTranscript
          class="h-full"
          :audio="audio"
          :transcript="transcript"
          :events="events"
          :disabled-play-tooltip="false"
          :show-duration="false"
          :show-play-button="false"
          :show-soundbar="false"
          @toggle-live-listening="emit('toggleListening')"
        >
          <template #empty-transcript>
            <div class="max-w-60">
              <OState
                type="empty"
                variant="2"
                :state="{
                  title: t('transcript_not_available'),
                  description: t('view_call_details_in_the_events_tab'),
                }"
              />
            </div>
          </template>

          <template #empty-events>
            <div class="max-w-60">
              <OState type="empty" variant="2" />
            </div>
          </template>
        </OTranscript>
      </div>
    </div>
  </ORightPanel>
</template>
